import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import authService from './authService';
import tokenManager from '../../utils/tokenManager';
import { onApiError, onApiRequest, onApiResponse } from '../../analytics/apiSignals';
import { normalizeApiErrorData, translateErrorMessage } from '../../utils/errorMessages';
import { saveReturnTo } from '../../utils/returnTo';

// グローバルなWindow型を拡張
declare global {
  interface Window {
    _authInitializing?: boolean;
  }
}

// リトライフラグを持つリクエスト設定の拡張型
export interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// バックエンドのURLを環境変数から取得
// 行動分析の所要時間計測用に、リクエスト設定へ開始時刻を持たせる
type TimedRequestConfig = InternalAxiosRequestConfig & { metadata?: { startedAt: number } };

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
// APIのURLが既に/apiで終わっているかチェック
const baseURL = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
// Axiosインスタンスの作成
const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 600000 // 600秒（LP全体構成・画像生成など長時間のAI処理に対応）
});

// 認証初期化を待機する関数
const waitForAuthInitialization = async (): Promise<void> => {
  // window._authInitializingが存在し、trueの場合は認証初期化中
  if (window._authInitializing) {
    
    // 最大10回、100msごとに認証状態をチェック
    for (let i = 0; i < 10; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!window._authInitializing) {
        return;
      }
    }
    
  }
};

// リクエストインターセプター
axiosInstance.interceptors.request.use(
  async (config) => {
    // 特定のエンドポイントを除き、認証初期化が完了するまで待機
    // 認証関連のAPIリクエストは待機せずに実行する
    if (!config.url?.includes('auth/') && !config.url?.includes('login') && !config.url?.includes('register')) {
      await waitForAuthInitialization();
    }
    
    // トークンマネージャーからトークンを取得
    const token = tokenManager.getToken();

    // トークンがある場合はAuthorizationヘッダーに追加
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // パス正規化 - すべてのURLから先頭のスラッシュを削除
    if (config.url?.startsWith('/')) {
      config.url = config.url.substring(1); // '/auth/login' -> 'auth/login'
    }

    // 行動分析: 所要時間の起点と、生成開始 API の検出（計測失敗は無視）
    try {
      (config as TimedRequestConfig).metadata = { startedAt: Date.now() };
      onApiRequest(config.method ?? 'get', config.url ?? '');
    } catch {
      // 計測は UX に影響させない
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 認証切れで /login へ送る。ログイン後に元いた画面へ戻せるよう、遷移直前に現在地を控える。
 * 既にログイン系の画面にいるときは何もしない。
 */
const redirectToLogin = (): void => {
  if (window.location.pathname !== '/login' &&
      window.location.pathname !== '/admin/login' &&
      !window.location.pathname.startsWith('/auth/')) {
    saveReturnTo(window.location.pathname + window.location.search);
    window.location.href = '/login';
  }
};

/**
 * クレジット残高不足 (402) を画面側へ通知する CustomEvent 名。
 * detail には backend EnsureSufficientCredits が返す required_credits /
 * available_credits / wallet_breakdown が入る。購読側は CreditBadge。
 */
export const CREDIT_INSUFFICIENT_EVENT = 'credit-insufficient';

// レスポンスインターセプター
axiosInstance.interceptors.response.use(
  (response) => {
    // 行動分析: 遅い API を検出（計測失敗は無視）。
    // 生成の完了/失敗はサーバー側（GenerationOutcomeRecorder）が記録するので
    // レスポンス本文は見ない。
    try {
      const config = response.config as TimedRequestConfig;
      const startedAt = config.metadata?.startedAt;
      onApiResponse(
        config.method ?? 'get',
        config.url ?? '',
        response.status,
        startedAt ? Date.now() - startedAt : 0
      );
    } catch {
      // 計測は UX に影響させない
    }
    return response;
  },
  async (error: AxiosError) => {
    // 行動分析: API エラー（401 のトークン更新経路は apiSignals 側で除外）
    try {
      const config = error.config as TimedRequestConfig | undefined;
      if (config) {
        const startedAt = config.metadata?.startedAt;
        onApiError(
          config.method ?? 'get',
          config.url ?? '',
          error.response?.status,
          startedAt ? Date.now() - startedAt : 0
        );
      }
    } catch {
      // 計測は UX に影響させない
    }

    // トライアル期限切れエラーの場合はカスタムイベントを発火して即座に返す
    if (error.response?.status === 403 && (error.response?.data as any)?.error === 'trial_expired') {
      window.dispatchEvent(new Event('trial_expired'));
      return Promise.reject(error);
    }

    // 402 Payment Required: クレジット残高不足
    // EnsureSufficientCredits ミドルウェアからの応答。
    // window.CustomEvent で各画面に通知し、CreditPurchaseModal などで購読する。
    if (error.response?.status === 402) {
      const data = error.response.data as
        | { error?: string; message?: string; details?: { required_credits?: number; available_credits?: number; wallet_breakdown?: Record<string, number> } }
        | undefined;
      if (data?.error === 'credit_insufficient') {
        window.dispatchEvent(new CustomEvent(CREDIT_INSUFFICIENT_EVENT, { detail: data.details ?? {} }));
        // 元のエラーはそのまま reject。呼び出し元で個別のフォールバック表示が必要なら handleApiError 側で拾える。
      }
    }

    if (error.response?.data) {
      error.response.data = normalizeApiErrorData(error.response.data);
    }
    (error as any).message = translateErrorMessage(error.message);

    // 認証クリーンアップ + redirect の対象判定:
    //
    // - 401 (Unauthorized): トークン無効 → refresh retry → 失敗時 logout
    // - 403 (Forbidden): 通常はアプリケーションレベルの認可エラー (権限なし) なので
    //   Promise.reject で component の catch に委ねる。logout しない。
    //
    // ただし `/auth/refresh` 自体が 403 を返す特殊ケース (backend
    // AuthController::refresh で `!$user->hasActiveOrganization()` 時に
    // 403 organization_inactive を返す) は例外。これは refresh token が有効でも
    // セッション継続不可能 = 認証データを全クリアして /login へ遷移すべき状態。
    // 通常の 403 と同列に扱うと、retry ロジック内で refresh が 403 を返した際に
    // access token のみクリアされて refresh token が残り、無限ループになる。
    //
    // 以前は 401/403 をすべて同列に扱っていたため、組織未所属ユーザーが商品登録
    // (/api/knowledge/products/url-register) など organization 必須エンドポイント
    // を叩いて 403 を受け取った瞬間に強制的に /login へ飛ばされる症状が出ていた
    // (本番ログで Google ログイン直後の連鎖を確認)。
    //
    // trial_expired の 403 は本ハンドラ前段 (line 85-88) で trial_expired イベ
    // ント発火に分岐済みなので、ここに到達しない。
    const isAuthRefreshFailure =
      error.response?.status === 403 &&
      typeof error.config?.url === 'string' &&
      error.config.url.includes('auth/refresh');

    // refresh API 自身が 403 を返した場合は即座に全クリア + /login 遷移。
    // refresh のリトライを試みると無限再帰になる (このinterceptorが再び発火し、
    // 再度 refresh を呼ぶ) ので、retry ロジックには入らずに専用分岐で処理する。
    if (isAuthRefreshFailure) {
      tokenManager.clearAllAuthData();
      redirectToLogin();
      return Promise.reject(error);
    }

    // `/auth/refresh` 自身の 401 は authService.refreshToken() が判定する
    // (別タブが先に回転させた場合は消さずにストレージの新しいトークンを使う)。
    // ここで全クリアすると、その判定の前に認証情報が消えて /login へ飛んでしまう。
    const isAuthRefreshRequest =
      typeof error.config?.url === 'string' && error.config.url.includes('auth/refresh');

    if (error.response?.status === 401 && !isAuthRefreshRequest) {

      // リクエスト情報を保存
      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      // まずリフレッシュトークンを確認 (更新された状態を取得)
      const refreshToken = tokenManager.getRefreshToken();
      
      // リフレッシュトークンがある場合
      if (refreshToken) {
        // まだリトライしていない場合はリフレッシュを試みる
        if (originalRequest && !originalRequest._retry) {
          
          // リトライフラグを設定
          originalRequest._retry = true;

          // 送信後に別のリクエスト（または別タブ）がリフレッシュを済ませていれば、
          // 新しいトークンで再送するだけでよい。もう一度回転させる必要はない。
          const currentToken = tokenManager.getToken();
          const sentAuthorization = originalRequest.headers?.['Authorization'];
          if (currentToken && originalRequest.headers && sentAuthorization !== `Bearer ${currentToken}`) {
            originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
            return axiosInstance(originalRequest);
          }
          
          try {
            // トークンリフレッシュを試みる
            const authService = (await import('./authService')).default;
            const response = await authService.refreshToken();
            
            
            if (response && response.token) {
              
              // 新しいトークンをヘッダーに設定
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${response.token}`;
              }
              
              // 元のリクエストを再試行
              return axiosInstance(originalRequest);
            }

            // リフレッシュがサーバーに拒否された（401/422）場合だけ authService が
            // 認証データを消している。そのときはログイン画面へ送る。
            // 通信断・5xx では何も消さずに元のエラーを返す（回線が戻れば次の 401 で再試行できる）。
            if (!tokenManager.getRefreshToken()) {
              redirectToLogin();
            }
          } catch (refreshError) {
            // エラーでもリフレッシュトークンは保持
          }
        } else if (originalRequest && originalRequest._retry) {
          // リフレッシュトークンはあるがリトライ済みの場合、リフレッシュトークンも無効の可能性
          tokenManager.clearAllAuthData();
          
          // ログインページにリダイレクト
          redirectToLogin();
        }
      } else {
        // リフレッシュトークンがない場合のみ全クリアしてリダイレクト
        tokenManager.clearAllAuthData();
        
        // ページ遷移時のみリダイレクト（API呼び出しでは行わない）
        
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

// APIリクエスト用のラッパー関数
export const apiRequest = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.get<T, AxiosResponse<T>>(url, config)
      .then(response => response.data);
  },

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.post<T, AxiosResponse<T>>(url, data, config)
      .then(response => response.data);
  },

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.put<T, AxiosResponse<T>>(url, data, config)
      .then(response => response.data);
  },

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.patch<T, AxiosResponse<T>>(url, data, config)
      .then(response => response.data);
  },

  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.delete<T, AxiosResponse<T>>(url, config)
      .then(response => response.data);
  }
};

export default axiosInstance;
